import common from '@common/common'
import { apis } from '@common/config'
import Upload from '@common/newUpload'

/**
 * 通知后端清理已上传的分片（避免孤儿分片残留占用存储）
 * 引擎的 cancelUpload 只在前端 abort xhr，cancel_url 本身引擎并未消费，
 * 故由调用方在取消/失败时主动调用本方法。
 * @param {String} token 分片上传 token
 */
export function cancelRemoteUpload(token) {
  if (!token) return Promise.resolve(null)
  return common.myFetch(apis.newUploadcancel, { token: token })
}

/**
 * 拉取分片上传配置（newUploadConfig）
 * 复用「我的素材-本地上传」的上传链路（uploadpro/upload/*）
 * @returns {Promise<Object>} 归一化后的上传配置
 */
export function fetchUploadConfig() {
  return common.myFetch(apis.newUploadConfig).then(r => {
    if (!r) return null

    return {
      check_url: r.check_url,
      token_url: r.token_url,
      upload_url: r.upload_url,
      // 兜底：newUpload.js 构造函数会用 max_part_num 直接覆盖 limit（并发上限），
      // 若后端不返回会导致 limit=undefined，并发判断异常，故此处必须有默认值
      max_part_size: (r.max_part_size || 10 * 1024 * 1024) / 1024 / 1024,
      max_part_num: r.max_part_num || 6,
      max_expire_time: r.max_expire_time,
      cancel_url: r.cancel_url,
      // 仅取图片格式（与「我的素材」区分：那里会 concat video/audio/image）
      imageTypes: (r.file_format && r.file_format.image) || []
    }
  })
}

/**
 * 创建一个「仅图片」的分片上传器实例
 * 与「我的素材」使用相同的 Upload 引擎，但：
 *  - allowedFileTypes 只允许图片
 *  - 挂载到调用方指定的 DOM id（避免和 myUploadBtn 冲突）
 *  - 不依赖 download.vue 上传浮窗，进度由调用方通过回调自行渲染
 *
 * @param {Object} options
 * @param {String} options.id                 挂载点 DOM id（需在调用方模板中预留 <div id="xxx"></div>）
 * @param {Function} [options.onReady]        文件已选中，开始上传前回调 (list) => void
 * @param {Function} [options.onProgress]     上传进度回调 (res) => void，res: {tid, name, progress, size}
 * @param {Function} [options.onComplete]     单个文件上传完成回调 (res) => void，res 含 tid/name/token 及 check_url 合并返回字段
 * @param {Function} [options.onError]        出错回调 (res) => void，res: {msg, tid, token}
 * @param {Function} [options.onCancel]       取消回调 (tid, token) => void
 * @returns {Promise<Upload|null>}
 */
export function createImageUploader(options = {}) {
  const { id, onReady, onProgress, onComplete, onError, onCancel } = options

  return fetchUploadConfig().then(cfg => {
    if (!cfg) return null

    const uploadConfig = {
      id: id,
      multipleUpload: true,
      chunkSize: 10,
      allowedFileTypes: cfg.imageTypes,
      check_url: cfg.check_url,
      token_url: cfg.token_url,
      upload_url: cfg.upload_url,
      max_part_size: cfg.max_part_size,
      max_part_num: cfg.max_part_num,
      max_expire_time: cfg.max_expire_time,
      cancel_url: cfg.cancel_url
    }

    const upload = new Upload(uploadConfig)

    if (onReady) upload.onReady(onReady)
    if (onProgress) upload.onProgress(onProgress)
    if (onComplete) upload.onComplete(onComplete)
    if (onError) upload.onError(onError)
    if (onCancel) upload.onCancel(onCancel)

    return upload
  })
}

export default {
  fetchUploadConfig,
  createImageUploader,
  cancelRemoteUpload
}
